import { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Camera, Calendar, ChevronRight } from "lucide-react";
import moment from "moment";
import { getTournamentList } from "../../api/tournaments";
import { ITournamentItem } from "../../models/ITournament";
import { Card, EmptyState, Loader, PageHeader, SearchInput, Segmented, Tag, Toolbar } from "../../ui";
import type { TagTone } from "../../ui";
import "./styles.scss";

type Bucket = "upcoming" | "active" | "past";

// Photographers manage photos for tournaments that have happened or are happening,
// so Active and Past come first; Upcoming (no photos yet) goes last.
const SECTIONS: { key: Bucket; label: string }[] = [
  { key: "active", label: "Active" },
  { key: "past", label: "Past" },
  { key: "upcoming", label: "Upcoming" },
];

const startOfDay = (value: string | Date): number => {
  const d = new Date(value);
  d.setHours(0, 0, 0, 0);
  return d.getTime();
};

const bucketOf = (t: ITournamentItem): Bucket => {
  const today = startOfDay(new Date());
  const start = startOfDay(t.startDate);
  const end = startOfDay(t.endDate);
  if (today < start) return "upcoming";
  if (today > end) return "past";
  return "active";
};

const formatDateRange = (start: string, end: string): string => {
  const s = moment(start);
  const e = moment(end);
  if (!s.isValid()) return "";
  if (!e.isValid() || s.isSame(e, "day")) return s.format("MMM D, YYYY");
  if (s.isSame(e, "month")) return `${s.format("MMM D")} – ${e.format("D, YYYY")}`;
  if (s.isSame(e, "year")) return `${s.format("MMM D")} – ${e.format("MMM D, YYYY")}`;
  return `${s.format("MMM D, YYYY")} – ${e.format("MMM D, YYYY")}`;
};

// The brand's sport accents: padel blue, volleyball orange, beach volleyball yellow; other sports stay grey.
const sportTone = (type: string): TagTone =>
  type === "Padel" ? "brand" : type === "Volleyball" ? "spark" : type === "Beach Volleyball" ? "yellow" : "neutral";

/**
 * Read-only grid of every tournament, grouped into Upcoming / Active / Old and
 * filterable by sport. Each card opens that tournament's gallery so a photographer
 * can upload/organize its photos. This is the photographer's landing page;
 * tournament managers and superadmins can reach it too.
 */
const Galleries = () => {
  const navigate = useNavigate();
  const hasFetched = useRef(false);

  const [loading, setLoading] = useState(true);
  const [tournaments, setTournaments] = useState<ITournamentItem[]>([]);
  const [search, setSearch] = useState("");
  const [sport, setSport] = useState("all");
  // Sections are collapsed by default; track which the user has opened.
  const [openSections, setOpenSections] = useState<{ [key: string]: boolean }>({});

  const toggleSection = (key: string) => setOpenSections((prev) => ({ ...prev, [key]: !prev[key] }));

  const fetchTournaments = async () => {
    setLoading(true);
    try {
      // Large page — the dashboard has well under 1000 tournaments. Newest first;
      // we re-group/sort client-side per section below.
      const res = await getTournamentList(1, 1000, undefined, "startDate", "desc");
      setTournaments(res.data?.items || []);
    } catch (error) {
      console.error("Error fetching tournaments:", error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    document.title = "Galleries - The Clash Egypt";
    if (hasFetched.current) return;
    hasFetched.current = true;
    fetchTournaments();
  }, []);

  // Sport options derived from the tournaments themselves (their `type`).
  const sports = useMemo(
    () => Array.from(new Set(tournaments.map((t) => t.type).filter(Boolean))).sort((a, b) => a.localeCompare(b)),
    [tournaments]
  );

  // Apply sport + search filters, then split into sections sorted latest-first.
  const sections = useMemo(() => {
    const q = search.trim().toLowerCase();
    const visible = tournaments.filter(
      (t) => (sport === "all" || t.type === sport) && (!q || t.name.toLowerCase().includes(q))
    );

    return SECTIONS.map((s) => ({
      ...s,
      items: visible
        .filter((t) => bucketOf(t) === s.key)
        .sort((a, b) => startOfDay(b.startDate) - startOfDay(a.startDate)),
    })).filter((s) => s.items.length > 0);
  }, [tournaments, search, sport]);

  const totalVisible = useMemo(() => sections.reduce((sum, s) => sum + s.items.length, 0), [sections]);

  // While text-searching, force every section open so matches aren't hidden.
  // The sport filter does NOT force-open — sections stay collapsible.
  const searchActive = search.trim() !== "";

  const renderCard = (t: ITournamentItem) => (
    <div
      key={t.id}
      className="gallery-card"
      onClick={() => navigate(`/dashboard/tournaments/${t.id}/gallery`)}
      role="button"
      tabIndex={0}
      onKeyDown={(e) => {
        if (e.key === "Enter" || e.key === " ") {
          e.preventDefault();
          navigate(`/dashboard/tournaments/${t.id}/gallery`);
        }
      }}
    >
      <div className="card-top">
        <span className="card-icon">
          <Camera size={16} />
        </span>
        <p className="card-name" data-tooltip={t.name}>
          {t.name}
        </p>
        <ChevronRight className="card-chevron" size={16} />
      </div>
      <div className="card-meta">
        {t.type && <Tag tone={sportTone(t.type)}>{t.type}</Tag>}
        <span className="card-date">
          <Calendar size={13} /> {formatDateRange(t.startDate, t.endDate)}
        </span>
      </div>
    </div>
  );

  return (
    <div className="galleries-page">
      <PageHeader eyebrow="Tournament" title="Galleries" />

      <Toolbar className="galleries-toolbar">
        <SearchInput
          block
          placeholder="Search tournaments..."
          value={search}
          onChange={setSearch}
        />
        {!loading && sports.length > 0 && (
          <Segmented
            ariaLabel="Sport"
            options={[{ value: "all", label: "All sports" }, ...sports.map((s) => ({ value: s, label: s }))]}
            value={sport}
            onChange={setSport}
          />
        )}
      </Toolbar>

      {loading ? (
        <Loader minHeight={360} />
      ) : totalVisible === 0 ? (
        <Card padding="none">
          <EmptyState
            icon={<Camera />}
            title={search.trim() || sport !== "all" ? "No tournaments match your filters." : "No tournaments yet."}
          />
        </Card>
      ) : (
        <div className="sections">
          {sections.map((sec) => {
            const isOpen = searchActive || !!openSections[sec.key];
            return (
              <section key={sec.key} className="gallery-section">
                {/* A disclosure row: the kit has no collapsible section, so it stays a plain button */}
                <button
                  type="button"
                  className="section-head"
                  onClick={() => toggleSection(sec.key)}
                  aria-expanded={isOpen}
                >
                  <ChevronRight className={`section-toggle ${isOpen ? "open" : ""}`} size={18} />
                  <h3>{sec.label}</h3>
                  <Tag tone="spark">{sec.items.length}</Tag>
                </button>
                {isOpen && <div className="gallery-grid">{sec.items.map(renderCard)}</div>}
              </section>
            );
          })}
        </div>
      )}
    </div>
  );
};

export default Galleries;
